import React, { useEffect, useState } from 'react';
import { solicitudAseoService } from '../services/solicitudAseoService';
import { SolicitudAseo, ActualizarSolicitudAseo } from '../types';
import DateRangeFilter, { DateFilterOptions } from '../components/DateRangeFilter';
import { getEffectiveDateRange, filterRegistrosByDateRange } from '../utils/dateFilterUtils';

const Solicitudes: React.FC = () => {
  const [solicitudes, setSolicitudes] = useState<SolicitudAseo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado] = useState('todos');
  const [dateFilter, setDateFilter] = useState<DateFilterOptions>({
    mode: 'preset',
    preset: 'todos'
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  useEffect(() => {
    loadSolicitudes();
  }, []);

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  const loadSolicitudes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await solicitudAseoService.getAll();
      setSolicitudes(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEstado = async (solicitud: SolicitudAseo, nuevaAtendida: boolean) => {
    try {
      setUpdatingId(solicitud.id);
      const updateData: ActualizarSolicitudAseo = {
        atendida: nuevaAtendida,
        observaciones: solicitud.observaciones,
      };
      const updated = await solicitudAseoService.update(solicitud.id, updateData);
      setSolicitudes(prev => prev.map(s => s.id === solicitud.id ? updated : s));
      setSuccessMessage(`Solicitud marcada como ${nuevaAtendida ? 'atendida' : 'pendiente'}`);
      setShowSuccessMessage(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al actualizar la solicitud');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Estás seguro de eliminar esta solicitud?')) return;
    
    try {
      await solicitudAseoService.delete(id);
      setSolicitudes(prev => prev.filter(s => s.id !== id));
      setSuccessMessage('Solicitud eliminada exitosamente');
      setShowSuccessMessage(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al eliminar la solicitud');
    }
  };

  const formatDateTime = (dateTime: string) => {
    try {
      // Asegurar que la fecha se parsee como UTC agregando 'Z' si no la tiene
      let dateString = dateTime;
      if (!dateTime.endsWith('Z') && dateTime.includes('T')) {
        dateString = dateTime + 'Z';
      }
      
      const date = new Date(dateString);
      
      // Usar toLocaleString con timeZone de Chile
      // Esto maneja automáticamente el horario de verano
      const formatted = date.toLocaleString('es-ES', {
        timeZone: 'America/Santiago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      return formatted;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateTime;
    }
  };

  const getEstadoBadge = (atendida?: boolean) => {
    return atendida 
      ? { label: 'Atendida', className: 'bg-green-100 text-green-800' }
      : { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800' };
  };

  // Aplicar filtros
  const filteredSolicitudes = solicitudes.filter(solicitud => {
    const matchesSearch = 
      solicitud.areaNombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      solicitud.observaciones?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Aplicar filtro de fechas
  const dateRange = getEffectiveDateRange(dateFilter);
  const solicitudesConFiltroFecha = filterRegistrosByDateRange(
    filteredSolicitudes.map(s => ({ ...s, fechaHoraInicio: s.fechaSolicitud })),
    dateRange
  ).map(s => {
    const { fechaHoraInicio, ...rest } = s as any;
    return rest as SolicitudAseo;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <div className="text-gray-500 mt-2">Cargando solicitudes...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2 sm:px-4 lg:px-8">
      <div className="py-6">
        {/* Mensaje de éxito */}
        {showSuccessMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Encabezado */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900">Solicitudes de Aseo</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Solicitudes reportadas desde códigos QR por pacientes y visitantes
              <span className="ml-2 text-blue-600 font-medium">• Ordenadas por fecha (más reciente primero)</span>
            </p>
          </div>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white shadow rounded-lg p-3 sm:p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Buscar por área u observaciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              />
            </div>
            {/* Filtro de estado eliminado por política */}
            <div className="flex items-end sm:col-span-1 lg:col-span-2">
              <div className="text-xs sm:text-sm text-gray-600">
                {solicitudesConFiltroFecha.length} solicitud{solicitudesConFiltroFecha.length !== 1 ? 'es' : ''} encontrada{solicitudesConFiltroFecha.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Filtro de fechas */}
        <DateRangeFilter
          value={dateFilter}
          onChange={setDateFilter}
          className="mb-6"
        />

        {/* Lista de solicitudes */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {solicitudesConFiltroFecha.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="text-gray-500">
                {searchTerm || filterEstado !== 'todos' || dateFilter.preset !== 'todos' || dateFilter.mode !== 'preset'
                  ? 'No se encontraron solicitudes que coincidan con los filtros.' 
                  : 'No hay solicitudes disponibles.'}
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {solicitudesConFiltroFecha.map((solicitud) => {
                const estadoBadge = getEstadoBadge(solicitud.atendida);
                return (
                  <li key={solicitud.id}>
                    <div className="px-3 py-3 sm:px-4 sm:py-4">
                      <div className="flex flex-col gap-3">
                        {/* Encabezado con área y fecha */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                                solicitud.atendida ? 'bg-green-500' : 'bg-yellow-500'
                              }`}>
                                <span className="text-white text-sm font-medium">
                                  {solicitud.areaNombre?.charAt(0).toUpperCase() || 'A'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-3 sm:ml-4">
                              <div className="text-sm sm:text-base font-medium text-gray-900">
                                {solicitud.areaNombre || `Área ${solicitud.areaId}`}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500">
                                {formatDateTime(solicitud.fechaSolicitud)}
                              </div>
                            </div>
                          </div>
                          
                          {/* Badge de estado */}
                          <div className={`inline-flex px-2 py-1 rounded-full text-xs sm:text-sm font-medium ${estadoBadge.className} self-start`}>
                            {estadoBadge.label}
                          </div>
                        </div>

                        {/* Observaciones */}
                        {solicitud.observaciones && (
                          <div className="ml-0 sm:ml-11 text-xs sm:text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                            <span className="font-medium">Observaciones:</span> {solicitud.observaciones}
                          </div>
                        )}

                        {/* Acciones */}
                        <div className="ml-0 sm:ml-11 flex flex-col sm:flex-row gap-2">
                          <button
                            onClick={() => handleUpdateEstado(solicitud, !solicitud.atendida)}
                            disabled={updatingId === solicitud.id}
                            className={`${
                              solicitud.atendida 
                                ? 'text-yellow-600 hover:text-yellow-900 bg-yellow-50 hover:bg-yellow-100' 
                                : 'text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100'
                            } text-sm font-medium py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                          >
                            {updatingId === solicitud.id ? (
                              <span className="flex items-center justify-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                Actualizando...
                              </span>
                            ) : (
                              solicitud.atendida ? 'Marcar como pendiente' : 'Marcar como atendida'
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(solicitud.id)}
                            disabled={updatingId === solicitud.id}
                            className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 text-sm font-medium py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Solicitudes;
